import {
    useCallback,
    useContext
} from 'react';
import {
    doc,
    setDoc,
    DocumentReference,
    writeBatch,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { debounce } from 'lodash';

import { firestore } from '../../services/startFirestore';
import { AgentType, DocDataWithId, DocId } from '#types/System.types';
import { UserContext } from '../../providers/UserContext';
import { MessageContext } from '../../providers/MessageContext';

/**
 * Interface for data set operations
 */
export interface UseSet {
    set: <T extends DocDataWithId = DocDataWithId>(path: string, docId: DocId, data: Partial<T>, agentType?: AgentType) => Promise<void>;
    debouncedSet: <T extends DocDataWithId = DocDataWithId>(path: string, docId: DocId, data: Partial<T>, agentType?: AgentType) => void;
    batchSet: (operations: Array<{ path: string; docId: DocId; data: any; agentType?: AgentType }>) => Promise<void>;
}

/**
 * Hook for managing Firestore data set operations
 * @param firestore - Firestore instance
 * @returns Object with set, debouncedSet, and batchSet functions
 */
export const useSet = (): UseSet => {
    /**
     * Sets data in Firestore
     * @param path - Document path
     * @param docId - Document ID
     * @param data - Data to set
     */

    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);

    const set = useCallback(async <T extends DocDataWithId = DocDataWithId>(path: string, docId: DocId, data: Partial<T>, agentType?: AgentType): Promise<void> => {
        const dataWithMeta = {
            ...data,
            meta: {
                ...data.meta,
                created: data.meta?.created instanceof Timestamp ? data.meta.created : serverTimestamp(),
                lastModified: serverTimestamp(),
                userId: user?.uid,
                agentType: agentType ?? AgentType.User,
            }
        };
        try {
            const docRef: DocumentReference = doc(firestore, path, docId);
            await setDoc(docRef, dataWithMeta, { merge: true });
        } catch (error) {
            throw handleFirestoreWriteError(error, '[useSet] set');
        }
    }, [handleFirestoreWriteError, user?.uid]);

    /**
     * Debounced version of set function
     * Saves values to the database after a delay
     * @param path - Document path
     * @param docId - Document ID
     * @param updates - Data to set
     * @param agentType - Optional agent type for metadata (defaults to User)
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSet = useCallback(
        debounce(<T extends DocDataWithId = DocDataWithId>(path: string, docId: DocId, updates: Partial<T>, agentType?: AgentType) => {
            void (async () => {
                try {
                    await set(path, docId, updates, agentType ?? AgentType.User);
                } catch (error) {
                    console.error('[useSet] debouncedSet operation failed after handler', error);
                }
            })();
        }, 350),
        [set]
    );

    /**
     * Performs batch set operations in Firestore
     * @param operations - Array of set operations
     * @example
     * const operations = [
     *   { path: 'users', docId: 'user1', data: { name: 'John Doe', age: 30 } },
     *   { path: 'posts', docId: 'post1', data: { title: 'New Post', content: 'Content here' } }
     * ];
     * await batchSet(operations);
     */
    const batchSet = useCallback(async (operations: Array<{ path: string; docId: DocId; data: any; agentType?: AgentType }>) => {
        const batch = writeBatch(firestore);
        try {
            operations.forEach(op => {
                const docRef = doc(firestore, op.path, op.docId);
                const dataWithMeta = {
                    ...op.data,
                    meta: {
                        ...op.data.meta,
                        created: op.data.meta?.created instanceof Timestamp ? op.data.meta.created : serverTimestamp(),
                        lastModified: serverTimestamp(),
                        userId: user?.uid,
                        agentType: op.agentType ?? AgentType.User,
                    }
                };
                batch.set(docRef, dataWithMeta, { merge: true });
            });
            await batch.commit();
        } catch (error) {
            throw handleFirestoreWriteError(error, '[useSet] batchSet');
        }
    }, [handleFirestoreWriteError, user?.uid]);

    return {
        set,
        debouncedSet,
        batchSet,
    };
};